import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { getUserData } from "../../../services/apiService";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import { isSessionSuperAdmin, resolvePlatformSuperAdminStaffId } from "../../../utils/permissionUtils";
import {
  fetchCustomerOrganisations,
  fetchOrgMessagingSettings,
  saveOrgMfaFlag,
  saveOrgMessagingSettings,
  saveOrgMultipleRemindersFlag,
  saveOrgQmsTrainingRolesFlag,
  saveOrgSponsorCroManagementFlag,
  syncOrgTwilioMessaging,
} from "../../../services/superAdminOrgService";
import SuperAdminFaqAiModal from "./SuperAdminFaqAiModal";
import SponsorCroStudyAccessEmailTemplatesPanel from "./SponsorCroStudyAccessEmailTemplatesPanel";
import {
  dialCodeForCountry,
  normalizeNationalMobileInput,
  orgMobileDialLabel,
  sanitizeNationalMobileInput,
} from "../../../utils/siteFieldValidationUtils";
import "./SuperAdminOrganisations.scss";
import "./SponsorCroStudyAccessEmailTemplatesPanel.scss";

const EMPTY_MESSAGING = {
  smsEnabled: false,
  whatsAppEnabled: false,
  hasTwilioSubaccount: false,
  canSendSms: false,
  canSendWhatsApp: false,
  twilioAccountSid: "",
  twilioMessagingServiceSid: "",
  smsFromNumber: "",
  whatsAppFromNumber: "",
  platformFromNumber: "",
  countryIso2: "GB",
  dialCode: "+44",
};

function toE164FromNational(national, countryIso2) {
  const iso = String(countryIso2 || "GB")
    .trim()
    .toUpperCase() || "GB";
  const digits = sanitizeNationalMobileInput(national, iso);
  if (!digits) return "";
  return `${dialCodeForCountry(iso)}${digits}`;
}

function toNationalFromStored(value, countryIso2) {
  const iso = String(countryIso2 || "GB")
    .trim()
    .toUpperCase() || "GB";
  return sanitizeNationalMobileInput(value, iso);
}

const FIELD_OPTIONS = [
  { key: "orgName", label: "Organisation" },
  { key: "noOfUsers", label: "Users" },
  { key: "noOfTrials", label: "Studies" },
  { key: "appointments", label: "Appointments" },
  { key: "volunteers", label: "Participants" },
  { key: "totalSms", label: "SMS" },
  { key: "mfa", label: "MFA" },
  { key: "qmsTrainingRoles", label: "Additional Training Roles" },
  { key: "sponsorCroManagement", label: "Sponsor/CRO management" },
  { key: "multipleReminders", label: "Multiple reminders" },
  { key: "faqAi", label: "FAQ AI" },
  { key: "status", label: "Status" },
];

const DEFAULT_COLUMNS = [
  "orgName",
  "noOfUsers",
  "noOfTrials",
  "mfa",
  "qmsTrainingRoles",
  "sponsorCroManagement",
  "multipleReminders",
  "faqAi",
  "status",
];

function SuperAdminOrganisations() {
  const { userData, loading: userLoading } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [rows, setRows] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [manageOrg, setManageOrg] = useState(null);
  const [messagingOrg, setMessagingOrg] = useState(null);
  const [emailTemplatesOrg, setEmailTemplatesOrg] = useState(null);
  const [draftQmsEnabled, setDraftQmsEnabled] = useState(false);
  const [draftSponsorCroEnabled, setDraftSponsorCroEnabled] = useState(false);
  const [draftMfaEnabled, setDraftMfaEnabled] = useState(true);
  const [draftMultipleReminders, setDraftMultipleReminders] = useState(false);
  const [faqAiOrg, setFaqAiOrg] = useState(null);
  const [savedMessaging, setSavedMessaging] = useState(EMPTY_MESSAGING);
  const [draftMessaging, setDraftMessaging] = useState(EMPTY_MESSAGING);
  const [messagingLoading, setMessagingLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMessaging, setSavingMessaging] = useState(false);
  const initialLoadStartedRef = useRef(false);

  const sessionUser = userData || getUserData();
  const isSuperAdmin = isSessionSuperAdmin(sessionUser);
  const accessResolved = !userLoading || Boolean(sessionUser);

  const loadOrgs = useCallback(async () => {
    if (!isSessionSuperAdmin(getUserData())) return;

    setOrgsLoading(true);
    try {
      const list = await fetchCustomerOrganisations();
      setRows(
        list.map((row) => ({
          ...row,
          status: row.inactive ? "Inactive" : "Active",
          mfa: row.mfaEnabled ? "On" : "Off",
          qmsTrainingRoles: row.qmsTrainingAdditionalRolesEnabled
            ? "On"
            : "Off",
          sponsorCroManagement: row.sponsorCroManagementEnabled ? "On" : "Off",
          multipleReminders: row.allowMultipleEPlannerReminders
            ? "On"
            : "Off",
          faqAi: row.faqAiEnabled ? "On" : "Off",
        })),
      );
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Could not load organisations.",
        type: "danger",
      });
    } finally {
      setOrgsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    if (!accessResolved || !isSuperAdmin || initialLoadStartedRef.current) {
      return;
    }
    initialLoadStartedRef.current = true;
    loadOrgs();
  }, [accessResolved, isSuperAdmin, loadOrgs]);

  const openManage = useCallback((row) => {
    setMessagingOrg(null);
    setManageOrg(row);
    setDraftQmsEnabled(!!row.qmsTrainingAdditionalRolesEnabled);
    setDraftSponsorCroEnabled(!!row.sponsorCroManagementEnabled);
    setDraftMfaEnabled(row.mfaEnabled !== false);
    setDraftMultipleReminders(!!row.allowMultipleEPlannerReminders);
  }, []);

  const closeManage = useCallback(() => {
    if (saving) return;
    setManageOrg(null);
  }, [saving]);

  const openFaqAi = useCallback((row) => {
    if (!row?.organizationId) return;
    setManageOrg(null);
    setFaqAiOrg(row);
  }, []);

  const closeFaqAi = useCallback(() => {
    setFaqAiOrg(null);
  }, []);

  const handleFaqAiSaved = useCallback((result) => {
    const nextEnabled = !!result?.enabled;
    const organizationId = result?.organizationId;
    setRows((prev) =>
      prev.map((row) =>
        row.organizationId === organizationId
          ? {
              ...row,
              faqAiEnabled: nextEnabled,
              faqAi: nextEnabled ? "On" : "Off",
            }
          : row,
      ),
    );
    setManageOrg((prev) =>
      prev && prev.organizationId === organizationId
        ? {
            ...prev,
            faqAiEnabled: nextEnabled,
            faqAi: nextEnabled ? "On" : "Off",
          }
        : prev,
    );
    setFaqAiOrg((prev) =>
      prev && prev.organizationId === organizationId
        ? {
            ...prev,
            faqAiEnabled: nextEnabled,
            faqAi: nextEnabled ? "On" : "Off",
          }
        : prev,
    );
    showAlert({
      title: "Saved",
      message: result?.message || "FAQ AI settings saved.",
      type: "success",
    });
  }, [showAlert]);

  const applyMessagingState = useCallback((messaging) => {
    const countryIso2 = messaging.countryIso2 || "GB";
    const platformFrom = String(messaging.platformFromNumber || "").trim();
    const mapped = {
      smsEnabled: messaging.smsEnabled,
      whatsAppEnabled: messaging.whatsAppEnabled,
      twilioMessagingServiceSid: messaging.twilioMessagingServiceSid,
      smsFromNumber: platformFrom,
      whatsAppFromNumber: platformFrom,
      platformFromNumber: platformFrom,
      hasTwilioSubaccount: messaging.hasTwilioSubaccount,
      twilioAccountSid: messaging.twilioAccountSid,
      canSendSms: messaging.canSendSms,
      canSendWhatsApp: messaging.canSendWhatsApp,
      countryIso2,
      dialCode: messaging.dialCode || dialCodeForCountry(countryIso2),
    };
    setSavedMessaging({
      ...messaging,
      ...mapped,
      smsFromNumberE164: platformFrom,
      whatsAppFromNumberE164: platformFrom,
    });
    setDraftMessaging(mapped);
  }, []);

  const openMessaging = useCallback(
    async (row) => {
      setManageOrg(null);
      setMessagingOrg(row);
      setSavedMessaging(EMPTY_MESSAGING);
      setDraftMessaging(EMPTY_MESSAGING);
      setMessagingLoading(true);
      try {
        let messaging = await fetchOrgMessagingSettings(row.organizationId);
        if (
          messaging.hasTwilioSubaccount &&
          !String(messaging.twilioMessagingServiceSid || "").trim()
        ) {
          try {
            const createdBy = resolvePlatformSuperAdminStaffId(userData);
            messaging = await syncOrgTwilioMessaging({
              organizationId: row.organizationId,
              createdBy,
            });
          } catch (syncError) {
            console.error(syncError);
            showAlert({
              title: "Warning",
              message:
                syncError?.message ||
                "Platform account loaded, but Messaging Service could not be refreshed yet.",
              type: "warning",
            });
          }
        }
        applyMessagingState(messaging);
      } catch (error) {
        console.error(error);
        showAlert({
          title: "Warning",
          message:
            error?.message ||
            "Could not load messaging settings for this organisation.",
          type: "warning",
        });
      } finally {
        setMessagingLoading(false);
      }
    },
    [showAlert, applyMessagingState, userData],
  );

  const closeMessaging = useCallback(() => {
    if (savingMessaging) return;
    setMessagingOrg(null);
  }, [savingMessaging]);

  const openEmailTemplates = useCallback((row) => {
    if (!row?.organizationId) return;
    setEmailTemplatesOrg(row);
  }, []);

  const closeEmailTemplates = useCallback(() => {
    setEmailTemplatesOrg(null);
  }, []);

  const hasTwilioSubaccount = !!(
    draftMessaging.hasTwilioSubaccount || savedMessaging.hasTwilioSubaccount
  );

  const dirty = useMemo(() => {
    if (!manageOrg) return false;
    return (
      draftQmsEnabled !== !!manageOrg.qmsTrainingAdditionalRolesEnabled ||
      draftSponsorCroEnabled !== !!manageOrg.sponsorCroManagementEnabled ||
      draftMfaEnabled !== (manageOrg.mfaEnabled !== false) ||
      draftMultipleReminders !== !!manageOrg.allowMultipleEPlannerReminders
    );
  }, [
    manageOrg,
    draftQmsEnabled,
    draftSponsorCroEnabled,
    draftMfaEnabled,
    draftMultipleReminders,
  ]);

  const messagingDirty = useMemo(() => {
    if (!messagingOrg || !hasTwilioSubaccount) return false;
    return (
      draftMessaging.smsEnabled !== savedMessaging.smsEnabled ||
      draftMessaging.whatsAppEnabled !== savedMessaging.whatsAppEnabled ||
      String(draftMessaging.twilioMessagingServiceSid || "") !==
        String(savedMessaging.twilioMessagingServiceSid || "")
    );
  }, [messagingOrg, hasTwilioSubaccount, draftMessaging, savedMessaging]);

  const handleSave = useCallback(async () => {
    if (!manageOrg?.organizationId || !dirty) return;
    setSaving(true);
    try {
      showLoader("Saving…");
      const createdBy = resolvePlatformSuperAdminStaffId(userData);
      const messages = [];

      let nextQms = !!manageOrg.qmsTrainingAdditionalRolesEnabled;
      let nextSponsorCro = !!manageOrg.sponsorCroManagementEnabled;
      let nextMfa = manageOrg.mfaEnabled !== false;
      let nextMultipleReminders = !!manageOrg.allowMultipleEPlannerReminders;

      if (draftQmsEnabled !== !!manageOrg.qmsTrainingAdditionalRolesEnabled) {
        const result = await saveOrgQmsTrainingRolesFlag({
          organizationId: manageOrg.organizationId,
          enabled: draftQmsEnabled,
          createdBy,
        });
        nextQms = result.enabled;
        messages.push(result.message);
      }

      if (draftSponsorCroEnabled !== !!manageOrg.sponsorCroManagementEnabled) {
        const result = await saveOrgSponsorCroManagementFlag({
          organizationId: manageOrg.organizationId,
          enabled: draftSponsorCroEnabled,
          createdBy,
        });
        nextSponsorCro = result.enabled;
        messages.push(result.message);
      }

      if (draftMfaEnabled !== (manageOrg.mfaEnabled !== false)) {
        const result = await saveOrgMfaFlag({
          organizationId: manageOrg.organizationId,
          enabled: draftMfaEnabled,
          createdBy,
        });
        nextMfa = result.enabled;
        messages.push(result.message);
      }

      if (draftMultipleReminders !== !!manageOrg.allowMultipleEPlannerReminders) {
        const result = await saveOrgMultipleRemindersFlag({
          organizationId: manageOrg.organizationId,
          enabled: draftMultipleReminders,
          createdBy,
        });
        nextMultipleReminders = result.enabled;
        messages.push(result.message);
      }

      setRows((prev) =>
        prev.map((row) =>
          row.organizationId === manageOrg.organizationId
            ? {
                ...row,
                qmsTrainingAdditionalRolesEnabled: nextQms,
                qmsTrainingRoles: nextQms ? "On" : "Off",
                sponsorCroManagementEnabled: nextSponsorCro,
                sponsorCroManagement: nextSponsorCro ? "On" : "Off",
                mfaEnabled: nextMfa,
                mfa: nextMfa ? "On" : "Off",
                allowMultipleEPlannerReminders: nextMultipleReminders,
                multipleReminders: nextMultipleReminders ? "On" : "Off",
              }
            : row,
        ),
      );
      setManageOrg((prev) =>
        prev
          ? {
              ...prev,
              qmsTrainingAdditionalRolesEnabled: nextQms,
              qmsTrainingRoles: nextQms ? "On" : "Off",
              sponsorCroManagementEnabled: nextSponsorCro,
              sponsorCroManagement: nextSponsorCro ? "On" : "Off",
              mfaEnabled: nextMfa,
              mfa: nextMfa ? "On" : "Off",
              allowMultipleEPlannerReminders: nextMultipleReminders,
              multipleReminders: nextMultipleReminders ? "On" : "Off",
            }
          : null,
      );
      setDraftQmsEnabled(nextQms);
      setDraftSponsorCroEnabled(nextSponsorCro);
      setDraftMfaEnabled(nextMfa);
      setDraftMultipleReminders(nextMultipleReminders);

      showAlert({
        title: "Saved",
        message: messages.join(" ") || "Saved.",
        type: "success",
      });
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Error",
        message: error?.message || "Could not save flag.",
        type: "danger",
      });
    } finally {
      hideLoader();
      setSaving(false);
    }
  }, [
    manageOrg,
    dirty,
    draftQmsEnabled,
    draftSponsorCroEnabled,
    draftMfaEnabled,
    draftMultipleReminders,
    userData,
    showLoader,
    hideLoader,
    showAlert,
  ]);

  const handleSaveMessaging = useCallback(async () => {
    if (!messagingOrg?.organizationId || !messagingDirty) return;
    setSavingMessaging(true);
    try {
      showLoader("Saving…");
      const createdBy = resolvePlatformSuperAdminStaffId(userData);
      const messagingResult = await saveOrgMessagingSettings({
        organizationId: messagingOrg.organizationId,
        smsEnabled: draftMessaging.smsEnabled,
        whatsAppEnabled: draftMessaging.whatsAppEnabled,
        twilioMessagingServiceSid: draftMessaging.twilioMessagingServiceSid,
        createdBy,
      });
      applyMessagingState(messagingResult);
      showAlert({
        title: "Saved",
        message: messagingResult.message || "Messaging settings saved.",
        type: "success",
      });
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Error",
        message: error?.message || "Could not save messaging settings.",
        type: "danger",
      });
    } finally {
      hideLoader();
      setSavingMessaging(false);
    }
  }, [
    messagingOrg,
    messagingDirty,
    draftMessaging,
    applyMessagingState,
    userData,
    showLoader,
    hideLoader,
    showAlert,
  ]);

  const getFieldLabel = useCallback((key) => {
    const match = FIELD_OPTIONS.find((f) => f.key === key);
    return match?.label || key;
  }, []);

  const renderOnOffBadge = (on) =>
    on ? (
      <span className="badge bg-success">On</span>
    ) : (
      <span className="badge bg-secondary">Off</span>
    );

  const tableActions = useMemo(
    () => [
      {
        label: "Manage",
        icon: "fas fa-sliders-h",
        onClick: openManage,
        visible: (row) => !!row.organizationId,
      },
      {
        label: "Manage FAQ AI",
        icon: "fas fa-comments",
        onClick: openFaqAi,
        visible: (row) => !!row.organizationId,
      },
      {
        label: "Text message communication",
        icon: "fas fa-sms",
        onClick: openMessaging,
        visible: (row) => !!row.organizationId,
      },
    ],
    [openManage, openFaqAi, openMessaging],
  );

  const renderCell = useCallback((row, field) => {
    if (field === "orgName") {
      return (
        <span className="super-admin-orgs__name" title={row.orgName}>
          {row.orgName || "—"}
        </span>
      );
    }
    if (field === "status") {
      return row.inactive ? (
        <span className="badge bg-secondary">Inactive</span>
      ) : (
        <span className="badge bg-success">Active</span>
      );
    }
    if (field === "mfa") {
      return renderOnOffBadge(row.mfaEnabled !== false);
    }
    if (field === "qmsTrainingRoles") {
      return renderOnOffBadge(!!row.qmsTrainingAdditionalRolesEnabled);
    }
    if (field === "sponsorCroManagement") {
      return renderOnOffBadge(!!row.sponsorCroManagementEnabled);
    }
    if (field === "multipleReminders") {
      return renderOnOffBadge(!!row.allowMultipleEPlannerReminders);
    }
    if (field === "faqAi") {
      return renderOnOffBadge(!!row.faqAiEnabled);
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  if (!accessResolved) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/notification-center" replace />;
  }

  return (
    <div className="super-admin-orgs p-2">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Customer Organisations" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: "Refresh",
            onClick: loadOrgs,
          },
        ]}
      />

      <div className="super-admin-orgs__intro">
        <i className="fas fa-sitemap" aria-hidden="true" />
        <div>
          <strong>AQ platform administration</strong>
          <p>
            Manage customer organisations from AscensionQ. You are not acting as
            a customer Admin — configure platform features per organisation
            below.
          </p>
        </div>
      </div>

      <ReportTable
        rows={rows}
        fieldOptions={FIELD_OPTIONS}
        defaultColumns={DEFAULT_COLUMNS}
        getFieldLabel={getFieldLabel}
        renderCell={renderCell}
        rowActions={tableActions}
        pageSizeOptions={[10, 25, 50, 100]}
        frozenColumnWidthRem={14}
        persistLayoutKey="super-admin-customer-orgs-v7"
        loading={orgsLoading}
      />

      {manageOrg ? (
        <>
          <button
            type="button"
            className="super-admin-orgs__drawer-backdrop"
            aria-label="Close manage panel"
            onClick={closeManage}
          />
          <aside
            className="super-admin-orgs__drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="super-admin-org-manage-title"
          >
            <header className="super-admin-orgs__drawer-header">
              <div>
                <h2 id="super-admin-org-manage-title">
                  Manage {manageOrg.orgName}
                </h2>
                <p className="super-admin-orgs__drawer-meta">
                  Organisation ID: {manageOrg.organizationId || "—"}
                  <span
                    className={`badge ms-2 ${
                      manageOrg.inactive ? "bg-secondary" : "bg-success"
                    }`}
                  >
                    {manageOrg.inactive ? "Inactive" : "Active"}
                  </span>
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={closeManage}
                disabled={saving}
              />
            </header>

            <div className="super-admin-orgs__drawer-body">
              <section className="super-admin-orgs__config-block">
                <h3>Security</h3>
                <div className="super-admin-orgs__flag-row">
                  <div>
                    <strong>Multi-factor authentication (MFA)</strong>
                    <p>
                      When On, users must complete MFA when entering this
                      organisation (including org switch). Default is On for
                      every organisation.
                    </p>
                  </div>
                  <div className="form-check form-switch super-admin-orgs__switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="sa-org-mfa"
                      checked={draftMfaEnabled}
                      disabled={saving}
                      onChange={(e) => setDraftMfaEnabled(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="sa-org-mfa">
                      {draftMfaEnabled ? "On" : "Off"}
                    </label>
                  </div>
                </div>
              </section>

              <section className="super-admin-orgs__config-block">
                <h3>FAQ assistant</h3>
                <div className="super-admin-orgs__email-templates-row">
                  <div>
                    <strong>FAQ AI</strong>
                    <p>
                      Turn the how-to assistant On or Off and set daily ask
                      limits for this organisation. The assistant generates
                      answers from AQ Trials product knowledge. Usage, cost,
                      and rejected asks are also managed here.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={saving}
                    onClick={() => openFaqAi(manageOrg)}
                  >
                    Manage FAQ AI
                  </button>
                </div>
              </section>

              <section className="super-admin-orgs__config-block">
                <h3>Reminders</h3>
                <div className="super-admin-orgs__flag-row">
                  <div>
                    <strong>Multiple e-Planner reminder points</strong>
                    <p>
                      When On, this organisation can add more than one
                      e-Planner reminder (day, week, or month) before an
                      appointment. When Off, only a single reminder point is
                      used.
                    </p>
                  </div>
                  <div className="form-check form-switch super-admin-orgs__switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="sa-org-multiple-reminders"
                      checked={draftMultipleReminders}
                      disabled={saving}
                      onChange={(e) =>
                        setDraftMultipleReminders(e.target.checked)
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="sa-org-multiple-reminders"
                    >
                      {draftMultipleReminders ? "On" : "Off"}
                    </label>
                  </div>
                </div>
              </section>

              <section className="super-admin-orgs__config-block">
                <h3>QMS Configuration</h3>
                <div className="super-admin-orgs__flag-row">
                  <div>
                    <strong>Additional Training Roles</strong>
                    <p>
                      When On, this customer can assign additional training
                      roles on user profiles for QMS (SOPs, reading lists, and
                      quizzes in My Learnings). Turning Off hides the field —
                      existing training-role data is kept.
                    </p>
                  </div>
                  <div className="form-check form-switch super-admin-orgs__switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="sa-qms-training-roles"
                      checked={draftQmsEnabled}
                      disabled={saving}
                      onChange={(e) => setDraftQmsEnabled(e.target.checked)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="sa-qms-training-roles"
                    >
                      {draftQmsEnabled ? "On" : "Off"}
                    </label>
                  </div>
                </div>
              </section>

              <section className="super-admin-orgs__config-block">
                <h3>Study &amp; User Management</h3>
                <div className="super-admin-orgs__flag-row">
                  <div>
                    <strong>Sponsor/CRO management</strong>
                    <p>
                      When On, this organisation sees Admin &gt; Sponsor/CRO
                      management (add Sponsor/CRO users and assign study / site
                      / folder access). Turning Off hides the menu for that
                      organisation.
                    </p>
                  </div>
                  <div className="form-check form-switch super-admin-orgs__switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="sa-sponsor-cro-management"
                      checked={draftSponsorCroEnabled}
                      disabled={saving}
                      onChange={(e) =>
                        setDraftSponsorCroEnabled(e.target.checked)
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="sa-sponsor-cro-management"
                    >
                      {draftSponsorCroEnabled ? "On" : "Off"}
                    </label>
                  </div>
                </div>
                {draftSponsorCroEnabled ? (
                  <div className="super-admin-orgs__email-templates-row">
                    <div>
                      <strong>Study access email templates</strong>
                      <p>
                        Default assign, update and remove emails for Sponsor/CRO
                        study sharing. Each organisation is seeded automatically;
                        edit here when you need org-specific wording.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={saving}
                      onClick={() => openEmailTemplates(manageOrg)}
                    >
                      Manage templates
                    </button>
                  </div>
                ) : null}
              </section>
            </div>

            <footer className="super-admin-orgs__drawer-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeManage}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!dirty || saving}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </footer>
          </aside>
        </>
      ) : null}

      {messagingOrg ? (
        <div
          className="modal fade show d-block super-admin-orgs__messaging-modal"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="super-admin-org-messaging-title"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5
                    className="modal-title"
                    id="super-admin-org-messaging-title"
                  >
                    Text message communication
                  </h5>
                  <p className="super-admin-orgs__drawer-meta mb-0 mt-1">
                    {messagingOrg.orgName || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeMessaging}
                  disabled={savingMessaging}
                />
              </div>

              <div className="modal-body">
                {messagingLoading ? (
                  <p className="super-admin-orgs__hint mb-0">
                    Loading messaging settings…
                  </p>
                ) : (
                  <>
                    <section className="super-admin-orgs__config-block">
                      <h3>Platform</h3>
                      {hasTwilioSubaccount ? (
                        <div className="super-admin-orgs__platform-summary">
                          <span className="badge bg-success">Ready</span>
                          <span className="super-admin-orgs__platform-from">
                            From{" "}
                            <strong>
                              {draftMessaging.platformFromNumber || "—"}
                            </strong>
                          </span>
                        </div>
                      ) : (
                        <p className="super-admin-orgs__hint mb-0">
                          Platform Twilio is not configured in API Web.config.
                        </p>
                      )}
                    </section>

                    <section
                      className={`super-admin-orgs__config-block${
                        hasTwilioSubaccount
                          ? ""
                          : " super-admin-orgs__config-block--locked"
                      }`}
                    >
                      <h3>This organisation</h3>
                      <div className="super-admin-orgs__messaging-grid">
                        <div className="super-admin-orgs__flag-row">
                          <strong>SMS</strong>
                          <div className="form-check form-switch super-admin-orgs__switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="sa-org-sms"
                              checked={!!draftMessaging.smsEnabled}
                              disabled={
                                !hasTwilioSubaccount ||
                                savingMessaging ||
                                messagingLoading
                              }
                              onChange={(e) =>
                                setDraftMessaging((prev) => ({
                                  ...prev,
                                  smsEnabled: e.target.checked,
                                }))
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="sa-org-sms"
                            >
                              {draftMessaging.smsEnabled ? "On" : "Off"}
                            </label>
                          </div>
                        </div>

                        <div className="super-admin-orgs__flag-row">
                          <strong>WhatsApp</strong>
                          <div className="form-check form-switch super-admin-orgs__switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="sa-org-whatsapp"
                              checked={!!draftMessaging.whatsAppEnabled}
                              disabled={
                                !hasTwilioSubaccount ||
                                savingMessaging ||
                                messagingLoading
                              }
                              onChange={(e) =>
                                setDraftMessaging((prev) => ({
                                  ...prev,
                                  whatsAppEnabled: e.target.checked,
                                }))
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="sa-org-whatsapp"
                            >
                              {draftMessaging.whatsAppEnabled ? "On" : "Off"}
                            </label>
                          </div>
                        </div>
                      </div>
                    </section>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeMessaging}
                  disabled={savingMessaging}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveMessaging}
                  disabled={
                    !hasTwilioSubaccount ||
                    !messagingDirty ||
                    savingMessaging ||
                    messagingLoading
                  }
                >
                  {savingMessaging ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {messagingOrg ? (
        <div
          className="modal-backdrop fade show"
          onClick={() => {
            if (!savingMessaging) closeMessaging();
          }}
          aria-hidden="true"
        />
      ) : null}

      <CtmsWorkspaceModal
        open={Boolean(emailTemplatesOrg)}
        title={
          emailTemplatesOrg
            ? `Study access email templates — ${emailTemplatesOrg.orgName || "Organisation"}`
            : "Study access email templates"
        }
        titleId="super-admin-org-email-templates-title"
        onClose={closeEmailTemplates}
        className="sponsor-cro-email-templates-host"
        dialogClassName="ctms-workspace-modal__dialog--full"
        contentClassName="sponsor-cro-email-templates-workspace"
      >
        {emailTemplatesOrg?.organizationId ? (
          <SponsorCroStudyAccessEmailTemplatesPanel
            key={emailTemplatesOrg.organizationId}
            organizationId={emailTemplatesOrg.organizationId}
            orgName={emailTemplatesOrg.orgName || ""}
          />
        ) : null}
      </CtmsWorkspaceModal>

      <SuperAdminFaqAiModal
        org={faqAiOrg}
        open={Boolean(faqAiOrg)}
        onClose={closeFaqAi}
        onSaved={handleFaqAiSaved}
        showAlert={showAlert}
      />

      <InfoAlert
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        onClose={hideAlert}
      />
    </div>
  );
}

export default SuperAdminOrganisations;
